import { computed, reactive, toRefs } from 'vue';
import localforage from 'localforage';
import workbox from '../registerServiceWorker';
import i18nInstance from '../plugins/i18n.plugin';
import vuetify from '../plugins/vuetify.plugin';
import { StatusEnum, VerticalPositionEnum } from '../constants';

const SETTINGS_STORE_ACTIVE_LANG_KEY = 'activeLang';
const SETTINGS_STORE_DARK_THEME_KEY = 'darkTheme';
const SETTINGS_STORE_PREFERRED_CAMERA_DEVICE_ID_KEY = 'preferredCameraDeviceId';

const settingsStore = localforage.createInstance({
    name: 'SettingsStore',
});

/** @type {MedForNgos.LangSettings} */
const elLang = {
    name: 'Ελληνικά',
    lang: 'el',
    isRtl: false,
    numberStringRepresentationLocale: 'el-GR',
};

/** @type {MedForNgos.LangSettings} */
const enLang = {
    name: 'English',
    lang: 'en',
    isRtl: false,
    numberStringRepresentationLocale: 'en-EN',
};

/** @returns {MedForNgos.NotificationSettings} */
const getNotificationInitialState = () => {
    return {
        show: false,
        status: 'ERROR',
        text: '',
    };
};

/** @type {import('vue').UnwrapRef<MedForNgos.SettingsState>} */
const settingsState = reactive({
    langs: [
        elLang,
        enLang,
    ],
    activeLang: elLang,
    notification: getNotificationInitialState(),
    newAppVersionAvailable: false,
    userWayInstance: null,
    isDarkTheme: vuetify.framework.theme.dark,
    preferredCameraDeviceId: null,
    currentTheme: computed(() => vuetify.framework.theme.themes[settingsState.isDarkTheme ? 'dark' : 'light']),
    isGreekLang: computed(() => settingsState.activeLang.lang === elLang.lang),
});

export default function useSettings() {
    const initSettingsModule = async() => {
        if (workbox) {
            workbox.addEventListener('waiting', () => {
                console.info('New app version is available for download.');
                settingsState.newAppVersionAvailable = true;
            });
        }

        /*
            NOTE: We run the command _setUserWayWidgetLocale twice to make sure the code works properly
            regardless of the execution time of await and 'userway:init_completed'
        */
        document.addEventListener('userway:init_completed', (event) => {
            // @ts-ignore
            settingsState.userWayInstance = event.detail.userWayInstance;

            _setUserWayWidgetLocale(settingsState.activeLang.lang);
        });

        const [activeLang, isDarkTheme, preferredCameraDeviceId] = await Promise.all([
            settingsStore.getItem(SETTINGS_STORE_ACTIVE_LANG_KEY),
            settingsStore.getItem(SETTINGS_STORE_DARK_THEME_KEY),
            settingsStore.getItem(SETTINGS_STORE_PREFERRED_CAMERA_DEVICE_ID_KEY),
        ]);

        // set locale
        settingsState.activeLang = activeLang || settingsState.activeLang;
        i18nInstance.locale = settingsState.activeLang.lang;
        _setUserWayWidgetLocale(settingsState.activeLang.lang);

        // set theme
        settingsState.isDarkTheme = isDarkTheme || settingsState.isDarkTheme;
        setTheme(settingsState.isDarkTheme);

        // set preferred camera device id
        settingsState.preferredCameraDeviceId = preferredCameraDeviceId || settingsState.preferredCameraDeviceId;
    };

    /** @param {MedForNgos.LangSettings} langSettings */
    const setLang = async(langSettings) => {
        settingsState.activeLang = langSettings;
        i18nInstance.locale = settingsState.activeLang.lang;

        _setUserWayWidgetLocale(settingsState.activeLang.lang);

        await settingsStore.setItem(SETTINGS_STORE_ACTIVE_LANG_KEY, settingsState.activeLang);
    };

    /** @param {string} lang */
    const _setUserWayWidgetLocale = (lang) => {
        settingsState.userWayInstance?.changeWidgetLanguage(lang);
    };

    /**
     * @param {string} translation
     * @param {any} [values]
     * @returns {import('vue-i18n').TranslateResult}
    */
    const getTranslation = (translation, values) => {
        const locale = settingsState.activeLang.lang ?? 'el';
        return i18nInstance.t(translation, locale, values);
    };

    /**
     * @param {string} translation
     * @param {number} choice
     * @param {any} [values]
     * @returns {import('vue-i18n').TranslateResult}
    */
    const getTcTranslation = (translation, choice, values) => {
        const locale = settingsState.activeLang.lang ?? 'el';
        return i18nInstance.tc(translation, choice, locale, values);
    };

    /** @param {MedForNgos.NotificationData} notification */
    const notify = (notification) => {
        settingsState.notification.show = true;
        settingsState.notification.text = notification.text;
        settingsState.notification.status = notification?.status ?? StatusEnum.Success;
        settingsState.notification.ms = notification?.ms;
        settingsState.notification.verticalPosition = notification?.verticalPosition ?? VerticalPositionEnum.Top;
    };

    const dismissNotification = () => {
        settingsState.notification = getNotificationInitialState();
    };

    const downloadNewAppVersion = async() => {
        if (!workbox) {
            console.error('Service worker not found on window.');
            return;
        }

        settingsState.newAppVersionAvailable = false;

        await workbox.messageSW({ type: 'SKIP_WAITING' });
    };

    /** @param {boolean} isDark */
    const toggleTheme = async(isDark) => {
        setTheme(isDark);
        await settingsStore.setItem(SETTINGS_STORE_DARK_THEME_KEY, isDark);
    };

    /** @param {boolean} isDark */
    const setTheme = (isDark) => {
        vuetify.framework.theme.dark = isDark;
    };

    /** @param {string} preferredDeviceId */
    const setPreferredCameraDeviceId = async(preferredDeviceId) => {
        settingsState.preferredCameraDeviceId = preferredDeviceId;
        await settingsStore.setItem(SETTINGS_STORE_PREFERRED_CAMERA_DEVICE_ID_KEY, preferredDeviceId);
    };

    const refsObject = toRefs(settingsState);

    return {
        ...refsObject,
        initSettingsModule,
        setLang,
        notify,
        dismissNotification,
        setPreferredCameraDeviceId,
        getTranslation,
        getTcTranslation,
        downloadNewAppVersion,
        toggleTheme,
    };
}

